import { Line as LineDrei } from '@react-three/drei';
import { GroupProps, useThree, useFrame } from '@react-three/fiber';
import { Vector3, type Vector3Tuple } from 'three';
import { useMemo, useState } from 'react';

import { Z } from 'modules/editor/constants';

import COLORS from 'styles/colors.module.scss';

import AnimatedLine from './AnimatedLine';

export interface LineProps extends GroupProps {
  gapSize?: number;
  positions: [Vector3Tuple, Vector3Tuple];
  spreadVector: Vector3Tuple;
  velocities: Vector3Tuple;
  width?: number;
  isActive?: boolean;
  isDashed?: boolean;
  isError?: boolean;
  isAnimationEnabled?: boolean;
}

const tempVec = new Vector3();
const tempDirection = new Vector3();
const tempPerpendicular = new Vector3();

const getPoints = (
  startPosition: Vector3,
  endPosition: Vector3,
  spreadVector: Vector3Tuple,
  scale: number,
  lineWidth: number,
  sceneWidth: number,
  gapSize: number
) => {
  const spreadVectorValues: number[] = spreadVector.filter((value) => value > 0);
  const spreadCount = spreadVectorValues.length;
  if (spreadCount === 1) {
    return [[startPosition, endPosition]];
  }

  tempVec.subVectors(endPosition, startPosition);
  const distance = tempVec.length();
  const invDistance = 1 / distance;
  const lengthOffset = Math.min(invDistance, 0.1);
  tempDirection.copy(tempVec).multiplyScalar(invDistance);

  const startSkewPosition = new Vector3().addVectors(
    startPosition,
    tempVec.copy(tempDirection).multiplyScalar(distance * lengthOffset)
  );
  const endSkewPosition = new Vector3().addVectors(
    startPosition,
    tempVec.copy(tempDirection).multiplyScalar(distance * (1 - lengthOffset))
  );

  tempPerpendicular.set(tempDirection.y, -tempDirection.x, tempDirection.z);

  const result: Vector3[][] = [];
  const halfGap = gapSize * 0.5;
  const initialSpreadVal = -(spreadCount - 1) * halfGap;
  const invSpreadDenom = 1 / (scale * sceneWidth * 0.02);
  const invOffsetDenom = 1 / (scale * sceneWidth * 0.0005 * (2400 / (lineWidth * 2)));

  let currentSpreadValue = 0;
  let previousSpreadValue = 0;

  for (let i = 0; i < spreadCount; i++) {
    currentSpreadValue += spreadVectorValues[i];

    const avgSpread = (currentSpreadValue + previousSpreadValue) * 0.5 - 0.5;
    const perpendicularOffset = avgSpread * invOffsetDenom + initialSpreadVal * invSpreadDenom + gapSize * invSpreadDenom * i;
    const initialOffset = avgSpread * invOffsetDenom;

    result.push([
      new Vector3().addVectors(startPosition, tempVec.copy(tempPerpendicular).multiplyScalar(initialOffset)),
      new Vector3().addVectors(startSkewPosition, tempVec.copy(tempPerpendicular).multiplyScalar(perpendicularOffset)),
      new Vector3().addVectors(endSkewPosition, tempVec.copy(tempPerpendicular).multiplyScalar(perpendicularOffset)),
      new Vector3().addVectors(endPosition, tempVec.copy(tempPerpendicular).multiplyScalar(initialOffset)),
    ]);
    previousSpreadValue = currentSpreadValue;
  }
  return result;
};

export function Line({
  gapSize = 0.2,
  width = 5,
  isActive = true,
  isDashed,
  isError,
  isAnimationEnabled = true,
  positions,
  spreadVector,
  velocities,
  ...rest
}: LineProps) {
  const { camera, size } = useThree();
  const [scale, setScale] = useState(camera.projectionMatrix.elements[0]);

  const points = useMemo(() => {
    const startPosition = new Vector3(...positions[0]);
    const endPosition = new Vector3(...positions[1]);
    return getPoints(startPosition, endPosition, spreadVector, scale, width, size.width, gapSize);
  }, [positions, size.width, width, spreadVector, gapSize, scale]);

  const { widthModifiers, velocityValues } = useMemo(() => {
    const widthModifiers: number[] = [];
    const velocityValues: number[] = [];
    for (let i = 0; i < 3; i++) {
      if (spreadVector[i] > 0) widthModifiers.push(spreadVector[i]);
      if (velocities[i] > 0) velocityValues.push(velocities[i]);
    }
    return { widthModifiers, velocityValues };
  }, [spreadVector, velocities]);

  useFrame(({ camera }) => {
    const newScale = camera.projectionMatrix.elements[0];
    if (newScale !== scale) {
      setScale(newScale);
    }
  });

  return (
    <group {...rest}>
      {!isDashed &&
        !isError &&
        points.map((point, index) => (
          <AnimatedLine
            key={index}
            points={point}
            opacity={isActive ? 1 : 0.4}
            lineWidth={Math.max(width * widthModifiers[index], 2)}
            renderOrder={Z.LINK}
            enabled={isAnimationEnabled}
            velocity={velocityValues[index]}
            transparent
          />
        ))}
      {(!!isDashed || !!isError) && (
        <LineDrei
          color={isError ? COLORS.red06 : COLORS.blue06}
          dashed={isDashed}
          dashScale={7}
          points={positions}
          opacity={isActive ? 1 : 0.4}
          lineWidth={2}
          renderOrder={Z.LINK}
          transparent
        />
      )}
      <LineDrei points={positions} opacity={0} lineWidth={Math.max(width, 15)} renderOrder={Z.LINK} transparent />
    </group>
  );
}
